import * as PIXI from "pixi";

import gameUiHtml from "bundle-text:./GameUI.pug";

import Game, { GameOverType } from "../game/Game";
import Keyboard from "../utils/Keyboard";
import Level from "../level/Level";
import { DeobfuPiouPiouzLevel } from "piou-core/types";
import Loader from "../Loader";
import { ACTIONS, ActionType } from "../game/actions";

const MIN_SCALE = 1;
const MAX_INIT_SCALE = Math.min(2, Loader.RESOLUTION);
const MAX_SCALE = Loader.RESOLUTION;

const ZOOM_IN_FACTOR = 1.1;
const ZOOM_OUT_FACTOR = 1 / ZOOM_IN_FACTOR;

export default class GameUI extends PIXI.Container {
  get uiWidth(): number {
    return this.game.app.renderer.width;
  }
  get uiHeight(): number {
    return this.game.app.renderer.height;
  }

  get gameWidth(): number {
    return this.game.level.lvlWidth;
  }
  get gameHeight(): number {
    return this.game.level.lvlHeight;
  }

  app: PIXI.Application;
  dom: HTMLElement;
  game: Game;

  gameContainer: PIXI.Container;

  constructor(
    app: PIXI.Application,
    lvlInfo: DeobfuPiouPiouzLevel,
    level: Level
  ) {
    super();
    this.app = app;
    this.game = new Game(app, lvlInfo, level);

    Keyboard.down(["NumpadAdd", "PageUp"], this.scaleUp.bind(this));
    Keyboard.down(["NumpadSubtract", "PageDown"], this.scaleDown.bind(this));
    this.game.app.ticker.add(() => {
      if (Keyboard.isDown("ArrowLeft")) {
        this.moveRight();
      }
      if (Keyboard.isDown("ArrowRight")) {
        this.moveLeft();
      }
      if (Keyboard.isDown("ArrowUp")) {
        this.moveDown();
      }
      if (Keyboard.isDown("ArrowDown")) {
        this.moveUp();
      }
    });

    this.gameContainer = new PIXI.Container();
    const border = new PIXI.Graphics()
      .lineStyle({ width: 1, color: 0 })
      .drawRect(0, 0, this.gameWidth, this.gameHeight);
    this.gameContainer.addChild(this.game, border);

    // TODO Center game container to piou positions, or just middle of level ?
    this.initPositionAndScale();

    this.addChild(this.gameContainer);
    app.stage.addChild(this);

    this.dom = this.app.view.parentElement!;
    this.initUI();

    this.game.on("uiUpdate", this.onUiUpdate.bind(this));
    this.game.on("endGame", this.onGameEnd.bind(this));
  }

  //#region Scale and move
  initPositionAndScale() {
    this.gameContainer.scale.set(
      Math.min(
        MAX_INIT_SCALE,
        Math.max(
          MIN_SCALE,
          this.uiWidth / this.gameWidth,
          this.uiHeight / this.gameHeight
        )
      )
    );
    this.fixPosition();
  }

  scaleUp() {
    const oldScale = this.gameContainer.scale.x;
    this.gameContainer.scale.set(
      Math.min(MAX_SCALE, this.gameContainer.scale.x * ZOOM_IN_FACTOR)
    );
    const realFactor = this.gameContainer.scale.x / oldScale;

    const zoomTargetX = this.uiWidth / 2;
    const zoomTargetY = this.uiHeight / 2;
    const dx = (zoomTargetX - this.gameContainer.x) * (realFactor - 1);
    const dy = (zoomTargetY - this.gameContainer.y) * (realFactor - 1);
    this.gameContainer.x -= dx;
    this.gameContainer.y -= dy;

    this.fixPosition();
  }

  scaleDown() {
    const oldScale = this.gameContainer.scale.x;
    this.gameContainer.scale.set(
      Math.max(MIN_SCALE, this.gameContainer.scale.x * ZOOM_OUT_FACTOR)
    );
    const realFactor = this.gameContainer.scale.x / oldScale;

    const zoomTargetX = this.uiWidth / 2;
    const zoomTargetY = this.uiHeight / 2;
    const dx = (zoomTargetX - this.gameContainer.x) * (realFactor - 1);
    const dy = (zoomTargetY - this.gameContainer.y) * (realFactor - 1);
    this.gameContainer.x -= dx;
    this.gameContainer.y -= dy;

    this.fixPosition();
  }

  moveLeft() {
    this.gameContainer.x -= 10;
    this.gameContainer.x = Math.max(
      this.uiWidth - this.gameWidth * this.gameContainer.scale.x,
      this.gameContainer.x
    );
    this.fixPosition();
  }

  moveRight() {
    this.gameContainer.x += 10;
    this.gameContainer.x = Math.min(0, this.gameContainer.x);
    this.fixPosition();
  }

  moveUp() {
    this.gameContainer.y -= 10;
    this.gameContainer.y = Math.max(
      this.uiHeight - this.gameHeight * this.gameContainer.scale.y,
      this.gameContainer.y
    );
    this.fixPosition();
  }

  moveDown() {
    this.gameContainer.y += 10;
    this.gameContainer.y = Math.min(0, this.gameContainer.y);
    this.fixPosition();
  }

  fixPosition() {
    if (this.gameWidth * this.gameContainer.scale.x < this.uiWidth) {
      this.gameContainer.x =
        (this.uiWidth - this.gameWidth * this.gameContainer.scale.x) / 2;
    } else {
      this.gameContainer.x = Math.min(
        0,
        Math.max(
          this.uiWidth - this.gameWidth * this.gameContainer.scale.x,
          this.gameContainer.x
        )
      );
    }
    if (this.gameHeight * this.gameContainer.scale.y < this.uiHeight) {
      this.gameContainer.y =
        (this.uiHeight - this.gameHeight * this.gameContainer.scale.y) / 2;
    } else {
      this.gameContainer.y = Math.min(
        0,
        Math.max(
          this.uiHeight - this.gameHeight * this.gameContainer.scale.y,
          this.gameContainer.y
        )
      );
    }
  }

  onResize() {
    this.fixPosition();
  }

  resize(width: number, height: number) {
    this.app.renderer.resize(width, height);
    this.onResize();
  }
  //#endregion

  //#region UI
  initUI() {
    const uiDom = document.createElement("div");
    uiDom.style.pointerEvents = "none";
    uiDom.style.position = "absolute";
    uiDom.style.top = "0";
    uiDom.style.left = "0";
    uiDom.style.width = "100%";
    uiDom.style.height = "100%";
    uiDom.innerHTML = gameUiHtml;
    this.dom.appendChild(uiDom);

    for (let i = 0; i < ACTIONS.length; i++) {
      const actionDom = this.dom.querySelector(`.action-${i + 1}`);
      if (!actionDom) continue;
      actionDom.querySelector(".count")!.textContent = "0";

      actionDom.addEventListener("click", () => {
        this.dom.querySelectorAll(".action").forEach((action) => {
          action.classList.remove("active");
        });
        actionDom.classList.add("active");
        this.game.actionManager.selectAction(i);
      });
    }

    this.onUiUpdate();
  }

  onUiUpdate(updateType?: "action" | "count") {
    this.dom.querySelector(".piou-count")!.textContent =
      this.game.piouCount.toString();
    this.dom.querySelector(".piou-goal")!.textContent =
      this.game.piouGoal.toString();

    // Add disabled to all .action
    this.dom.querySelectorAll(".action").forEach((action) => {
      action.classList.add("disabled");
    });

    for (let i = 0; i < ACTIONS.length; i++) {
      let count = this.game.actionManager.availableActions[i];
      if (count === undefined || count === 0) continue;
      const actionDom = this.dom.querySelector(`.action-${i + 1}`);
      if (!actionDom) {
        console.warn(`No action dom for type ${ActionType[i]}`);
        continue;
      }
      actionDom.classList.remove("disabled");
      actionDom.querySelector(".count")!.textContent = count.toString();
    }
  }

  onGameEnd(e: GameOverType) {
    switch (e) {
      case GameOverType.NO_PIOU:
      case GameOverType.LESS_THAN_GOAL:
        this.dom.querySelector(".lose")!.classList.remove("hidden");
        break;
      case GameOverType.GOAL:
      case GameOverType.MORE_THAN_GOAL:
        this.dom.querySelector(".win")!.classList.remove("hidden");
        break;
    }
  }
  //#endregion
}
